/**
 * ArchLang v0.10 — view projection stage, engine layer (S13b).
 *
 * `planView(view, ctx)` turns a parsed `ViewDeclV2` into a **`ViewProjectionPlan`**:
 * a set of qualified-name-keyed membership sets + rewrite directives the scene
 * layer (`applyProjection`, `@archlang/scene/arch-model/view-projection`) applies
 * to the board's scene `Module[]`/`DependencyEdge[]`. It is pure over the already
 * landed selector/expression evaluators (`evalSelector`, `evalExpression`) and the
 * derived graph — no scene types, no rendering.
 *
 * ## The engine↔scene key handoff (BLOCKER-1)
 * `planView` emits keys on the **dotted qualified name** — exactly what
 * `evalSelector` returns (`selector-eval.ts`, `ElementNode.key = path.join(".")`).
 * It does NOT emit `#id`s as keys. The scene side (`applyProjection`) matches each
 * scene `Module` back to its qualified key via `qualifiedKeyByModule` (NOT the
 * id-first `moduleKey`). Where a module carries a `#id`, the plan carries it
 * explicitly in {@link ViewProjectionPlan.stableIds} (qualified key → id) so the
 * downstream board seams (selection/drag/coloring/minimap/search/flyTo — wired in
 * S15) can bridge to their id-keyed channels. This is the projection's correctness
 * foundation: `moduleKey` (id-first) and the engine universe key (qualified-name)
 * diverge for every id-carrying module.
 *
 * ## Determinism
 * `planView` is a pure function of `(view, model, edges)`. Every emitted collection
 * is sorted (keys lexicographic, group values lexicographic, synthetic edge ids
 * derived deterministically). No `Math.random` / `Date`. Two `planView` calls over
 * the same inputs are deep-equal — the identity + snapshot gates depend on it.
 *
 * ## The identity signal
 * `planView(null, …)` and a view with no projection-affecting clause (only a
 * description / lens / representation clause) return **`null`** — never a fabricated
 * "all shown" plan. `null` is what lets S15 keep the default board byte-identical.
 */

import type {
  ViewDeclV2,
  ViewClause,
  StyleClause,
  StyleMember,
  StyleBundleDecl,
  KnobDecl,
  KnobBinding,
  AtClause,
  GroupByClause,
} from "./v010-ast.js";
import type { LifecycleWindow } from "./decorations.js";
import type { Selector, Expr, ExprGetter } from "./selector-ast.js";
import { EXPRESSION_FUNCTIONS } from "./selector-ast.js";
import type { Diagnostic, QualifiedName, SourceSpan, StableId, Value } from "./types.js";
import type { CallEdge, DependencyEdge } from "./graph.js";
import { isCallEdge } from "./graph.js";
import type { ElementNode, EvalContext } from "./selector-eval.js";
import {
  evalSelector,
  refNamesSomething,
  containmentMembers,
  UnsupportedSelectorError,
  SortMismatchError,
  BUILTIN_KINDS,
} from "./selector-eval.js";
import { evalExpression, UnsupportedExprError } from "./expression-eval.js";
import { PRIMARY_PLANE } from "./presence.js";
import {
  assignColorize,
  assignCss,
  assignDistinct,
  assignHeat,
  assignStyled,
  NEUTRAL_NO_DATA,
  type ColorLegendEntry,
} from "./view-style.js";
import { isCssColor } from "./palette.js";
import { makeDiag } from "./diagnostics/catalog.js";

// =============================================================================
// Plan shape
// =============================================================================

/**
 * A synthesized board edge the projection adds (`edgeLift.add`). Two provenances:
 *   - `"via-lift"`: A→B routed around one or more HIDDEN intermediates (`via`),
 *     replacing the dropped A→H…H→B chain.
 *   - `"group-copy"`: a fan-out duplicate of a real edge when an endpoint became a
 *     per-group copy (multi-value `group by`); `fromId`/`toId` carry the copy ids.
 * All keys are qualified names; `fromId`/`toId` are the ids to stamp on the scene
 * edge (a copy id, or a `StableId` for an id-carrying via-lift endpoint).
 */
export interface LiftedEdge {
  readonly edgeId: string;
  readonly fromKey: string;
  readonly toKey: string;
  readonly toInterfaceKey: string;
  /** Hidden intermediates removed by a via-lift (in path order); empty for a copy. */
  readonly via: readonly string[];
  readonly fromId?: string;
  readonly toId?: string;
  readonly origin: "via-lift" | "group-copy";
}

/** One synthesized `group by` container + its members (design §3). Nested
 *  `group by` (VIEWS §4 repetition) links levels via `parentId`; container
 *  ids carry the level path (`::group::1`, `::group::1.2`). */
export interface GroupAssignment {
  /** Synthetic container id (`g1`, `g2`, … in lexicographic value order). */
  readonly containerId: string;
  /** Display label — the getter value this group collects. */
  readonly label: string;
  /** Origin qualified keys re-parented DIRECTLY under the container (single-value
   *  members — no copy). */
  readonly memberKeys: readonly string[];
  /** Per-copy entries minted under the container (multi-value members). */
  readonly copies: readonly { readonly copyId: string; readonly originKey: string }[];
}

/** A node display overlay (`style <sel> { … }`). Color casts (colorize/heat/css)
 *  + legend are S14 — here `color` holds a literal / getter-string only. */
export interface NodeStyle {
  readonly color?: string;
  readonly rename?: string;
  readonly pin?: { readonly x: number; readonly y: number };
  /** Other `path: value` rewrites (widget-prop overlays); dotted path → text. */
  readonly overlay?: ReadonlyMap<string, string>;
}

/** A per-edge overlay (`style <edge-sel> { … }` / `style violating <P>`). */
export interface EdgeStyle {
  readonly color?: string;
  readonly width?: number;
}

/** One boundary port-nub: an absent peer, seen from the shown endpoint. */
export interface BoundaryStub {
  readonly peerKey: string;
  /** "out": shown → absent; "in": absent → shown. A peer connected both
   *  ways yields two stubs (the port model is direction-keyed). */
  readonly direction: "in" | "out";
  /** Dropped boundary call edges aggregated into this stub. */
  readonly count: number;
  /** `style … outside` overlay for this stub (VIEWS §7 third form). */
  readonly style?: EdgeStyle;
}

/** A shown endpoint with absent (not-shown, not-hidden) neighbors — rendered
 *  as port-nubs with count badges (VIEWS §7 default). `hide` clauses whose
 *  arrow touches `outside` suppress matching stubs (they hide the EDGE — the
 *  absent element was never in the selection, so there is nothing
 *  element-shaped to hide). */
export interface BoundaryMark {
  readonly nodeKey: string;
  readonly stubs: readonly BoundaryStub[];
}

/**
 * The projection plan — qualified-name-keyed. `null` from {@link planView} is the
 * identity signal (default board). See the module doc for the key contract.
 */
export interface ViewProjectionPlan {
  /** Every element key that survives to the board (modules + sub-elements). */
  readonly shown: ReadonlySet<string>;
  /** Explicitly `hide`-n keys (drives via-lift + re-parent, distinct from absent). */
  readonly hidden: ReadonlySet<string>;
  /**
   * Emphasis keys — the FIGURE of the view. Never membership: everything else
   * stays shown, as ground. Empty when the view wrote no `focus`.
   */
  readonly focus: ReadonlySet<string>;
  /**
   * Canonical edge ids (`edgeIdOf`) an edge-sort `focus` marked, both of whose
   * endpoints survived to `shown`. A node-sort `focus` leaves this empty — the
   * renderers derive a node-focused view's edge emphasis from the endpoints
   * (an edge is figure iff both ends are), and only an explicit `focus A > B`
   * can single out one wire between two figure nodes.
   */
  readonly focusEdges: ReadonlySet<string>;
  /**
   * The planes this projection narrowed to (`on <plane>` clauses), empty when
   * it didn't. `shown` already reflects them for every NODE — but a member
   * whose presence differs from its node's (§7 partial presence) can't be
   * expressed there: fields are node ATTRIBUTES, not universe nodes, so a
   * field key is never a `shown` candidate. The renderer joins a member's own
   * plane set against this instead.
   */
  readonly planes: ReadonlySet<string>;
  /**
   * Member keys the DEFAULT plane hides (`ctx.defaultPlane`, no `on` clause):
   * the ones a partial-presence block declared, which exist on their plane
   * only. Empty for every other projection — including an explicit `on` pick,
   * which answers the same question through `planes` above.
   *
   * Separate from `planes` because the default board is not a plane pick: it
   * keeps a plane-scoped node and its ordinary members, and removes only what
   * a block declared. A plane set can't say that — it would take the node too.
   */
  readonly planeHiddenMembers: ReadonlySet<string>;
  /**
   * Every shown element's resolved lifecycle window, keyed by qualified name —
   * already clamped to its containers.
   *
   * SCRUB-INDEPENDENT by construction, and that is the whole point: resolving a
   * window costs a walk, resolving a STATE from a window is one comparison. The
   * scrub point therefore stays OUT of this plan (and out of the memo key that
   * caches it), and `lifecycleStateAt` runs per scrub tick outside it. Had the
   * scrub point been an ordinary knob, every frame of a drag would have busted
   * the projection memo and re-run the whole pipeline.
   *
   * The windows themselves are NOT carried here: they key on the shown set,
   * while every consumer keys on `moduleKey` (a stable `#id` when there is
   * one), so all three read `lifecycleWindowsOf(model)` instead and this map
   * was computed per view for nobody.
   */
  /** The view's declared `at` point, if it wrote one. Drives the INITIAL
   *  scrubber position; live scrubbing overrides it session-ephemerally. */
  readonly at?: AtClause;
  /**
   * Effective parent of every shown MODULE = its nearest shown module ancestor
   * (`null` = board root). Where this differs from a module's natural parent, that
   * IS the hide mid-tree re-parent. A `group by` container overrides it downstream.
   */
  readonly parentOf: ReadonlyMap<string, string | null>;
  readonly groups: readonly GroupAssignment[];
  /** Shown modules with no `group by` value (the "ungrouped" pool). */
  readonly ungrouped: ReadonlySet<string>;
  /**
   * Every minted copy id → its origin qualified key (Major-6). ALL id-keyed board
   * seams (selection, drag/pins, coloring, minimap, search, flyTo) MUST resolve a
   * copy id back through this before use; model-channel writes always target the
   * origin, never a copy. Empty unless a multi-value `group by` minted copies.
   */
  readonly copyOrigin: ReadonlyMap<string, string>;
  readonly edgeLift: { readonly drop: ReadonlySet<string>; readonly add: readonly LiftedEdge[] };
  readonly styleIndex: ReadonlyMap<string, NodeStyle>;
  readonly edgeStyleIndex: ReadonlyMap<string, EdgeStyle>;
  readonly boundary: readonly BoundaryMark[];
  /** Qualified key → `#id`, for shown modules that carry one (BLOCKER-1 bridge). */
  readonly stableIds: ReadonlyMap<string, StableId>;
  /**
   * The style engine's color result (S14): qualified key → resolved CSS color
   * for every shown node a `style { color: <cast> }` clause colored. Feeds
   * S15's `NodeColoring` adapter; the scene `applyProjection` does NOT bake this
   * onto modules (color is a stroke overlay, not truth).
   */
  readonly colorResult: ReadonlyMap<string, string>;
  /** Ordered legend rows for {@link colorResult} — swatch / gradient / no-data.
   *  Engine-typed; S15's thin adapter maps it to the board `ColorLegendEntry`. */
  readonly legend: readonly ColorLegendEntry[];
  /**
   * `pin <x>,<y>` directives (S14): qualified key → board position. S15 feeds
   * this into the layout `placements` map (the same channel drags write). Not
   * applied by `applyProjection`.
   */
  readonly pins: ReadonlyMap<string, { readonly x: number; readonly y: number }>;
  /**
   * `style @@<key> { render: replicated }` — aspect plane → projection mode
   * (`web` default | `replicated` per-member duplicate). Drives the board
   * aspect overlay's shape; empty when no clause sets it. Not consumed by
   * `applyProjection` (render-only, off the structural mirror).
   */
  readonly renderModeByAspect: ReadonlyMap<string, "web" | "replicated">;
  /** Style-engine diagnostics (S14): color-cast-explicit, unknown bundle,
   *  unknown policy, unparseable css. Surfaced by S15; empty otherwise. */
  readonly diagnostics: readonly Diagnostic[];
}

// =============================================================================
// Edge identity — the engine↔scene bridge
// =============================================================================

/** The structural fields shared by an engine `CallEdge` and a scene
 *  `DependencyEdge` (verified 1:1 through `serializeEdges`). */
export interface EdgeKeyParts {
  readonly fromModulePath: readonly string[];
  readonly toModulePath: readonly string[];
  readonly toInterfacePath: readonly string[];
  readonly processName?: string;
  readonly stepIndex?: number;
  readonly altCondition?: string;
}

/**
 * A canonical, cross-layer edge id: the full structural identity of a call edge.
 * `planView` computes it over the engine graph; `applyProjection` (scene) computes
 * the byte-identical string over its scene edges — the two match because the scene
 * `DependencyEdge` mirrors the engine `CallEdge` fields exactly. Keep this in sync
 * with the scene mirror in `@archlang/scene/arch-model/view-projection`.
 */
export function edgeIdOf(e: EdgeKeyParts): string {
  // JSON array of the parts: unambiguous (no separator collisions), and an absent
  // field serializes to `null` deterministically. MUST match the scene mirror.
  return JSON.stringify([
    e.fromModulePath.join("."),
    e.toModulePath.join("."),
    e.toInterfacePath.join("."),
    e.processName ?? null,
    e.stepIndex ?? null,
    e.altCondition ?? null,
  ]);
}

// =============================================================================
// planView
// =============================================================================

/**
 * Clauses that project onto the board — a view lacking all of them ⇒ identity.
 *
 * `at` counts, for the same reason `on` does: the plan is the ONLY channel the
 * clause reaches the board through (`plan.at` seeds the scrubber), so an
 * `at`-only view returning identity made `view ToBe { at v3 }` paint exactly
 * like the bare board — the clause was decor. It does not narrow `shown`: the
 * plan never filters by lifecycle (that is the scrubber's job, see `AtClause`),
 * so the resulting plan is the identity plan plus the point pair.
 */
const PROJECTING_CLAUSES: ReadonlySet<ViewClause["kind"]> = new Set([
  "show",
  "hide",
  "focus",
  "groupBy",
  "style",
  "on",
  "at",
]);

const PRIMARY_ONLY: ReadonlySet<string> = new Set([PRIMARY_PLANE]);

/** The module a node belongs to (itself if a module, else its nearest module
 *  ancestor) — for resolving plane presence, which is module-level (§1.2). */
function owningModuleKey(key: string, u: EvalContext["universe"]): string {
  const parts = key.split(".");
  while (parts.length > 0) {
    const k = parts.join(".");
    if (u.byKey(k)?.structuralKind === "module") return k;
    parts.pop();
  }
  return key;
}

export function planView(
  view: ViewDeclV2 | null,
  outerCtx: EvalContext,
  viewsByName?: ReadonlyMap<string, ViewDeclV2>,
  /** Click-reveal (VIEWS §7): qualified keys the user revealed from
   *  boundary stubs — unioned into `shown` AFTER hide subtraction and
   *  `on` plane narrowing (exact keys, no selector resolution). Hidden
   *  keys are skipped; unknown keys are ignored. Session state, owned by
   *  the shell. */
  revealedKeys?: ReadonlySet<string>,
): ViewProjectionPlan | null {
  if (!view) return null;

  // A view-instance (`view <Parent> <Name> { <bindings> <clauses> }`) resolves to
  // its effective merged view first: the parent's clauses + the instance's appended
  // clauses, carrying the parent's knob DECLS + the instance's knob BINDINGS. A plain
  // view passes through unchanged.
  const { view: effective, diagnostics: instanceDiags } = resolveViewInstance(view, viewsByName);
  // The default plane (§1.2) applies only to a view that names no plane of its
  // own — and to a bare board, which is a view with no clauses at all. Decided
  // off the EFFECTIVE clauses so a view INSTANCE inherits its parent's `on`.
  const defaultPlaneNarrows =
    !!outerCtx.defaultPlane &&
    !!outerCtx.presence?.planeExclusiveMembers.size &&
    !effective.clauses.some((c) => c.kind === "on");
  if (!effective.clauses.some((c) => PROJECTING_CLAUSES.has(c.kind)) && !defaultPlaneNarrows) return null;

  // A module-scoped view (v0.10 §5.2 — `ownerPath` stamped by the resolver)
  // resolves selector names from its module context first (§5.5 precedent):
  // owner chain walked outward, then the global fallback. Top-level views
  // (no ownerPath) evaluate byte-identically to before.
  const ctxBase: EvalContext =
    effective.ownerPath && effective.ownerPath.length > 0 ? { ...outerCtx, scopePath: effective.ownerPath } : outerCtx;

  // Bind the view/instance knob values into the eval context (§3, S18): `$knob`
  // atoms, `@@key:$knob`, and `within $knob` read `ctx.knobs`. Kind-check diagnostics
  // fold into the plan.
  const knobResult = resolveKnobBindings(effective, ctxBase.universe);
  const ctx: EvalContext = { ...ctxBase, knobs: knobResult.knobs };

  const u = ctx.universe;
  const allKeys = new Set(u.nodes.map((n) => n.key));
  const callEdges: CallEdge[] = (ctx.graph?.edges ?? []).filter(isCallEdge);

  // Evaluate a clause selector, converting an evaluator error (an unbound / wrong
  // knob, an unsupported/deferred atom) into a plan diagnostic instead of a throw —
  // the same non-fatal posture the `style` clauses already take. `undefined` = skip.
  const selDiags: Diagnostic[] = [];
  const safeResolve = (sel: Selector, shownArg: Set<string> | undefined, span: SourceSpan | undefined): SetResult | undefined => {
    // `in <Ref>` is the one predicate whose argument is an element ref, and an
    // unresolvable one evaluates to the empty set — a blank view with nothing
    // to read. Say so before evaluating.
    for (const bad of unresolvedInTargets(sel, ctx)) {
      const key = bad.reason === "unknown" ? "view.unknownInTarget" : "view.uncontainableInTarget";
      selDiags.push(
        makeDiag("warning", span, VIEW_UNKNOWN_IN_TARGET, key, { name: bad.path.join(".") }),
      );
    }
    try {
      return resolveSet(sel, ctx, shownArg);
    } catch (e) {
      if (e instanceof UnsupportedSelectorError || e instanceof SortMismatchError) {
        selDiags.push(makeDiag("warning", span, VIEW_SELECTOR_ERROR, "view.selectorError", { detail: e.message }));
        return undefined;
      }
      throw e;
    }
  };

  // --- 1. show / hide / focus ------------------------------------------------
  let shown: Set<string>;
  const hidden = new Set<string>();
  const focus = new Set<string>();
  const focusEdges = new Set<string>();
  const edgeDrop = new Set<string>();

  const showClauses = effective.clauses.filter((c) => c.kind === "show");
  const showKeys = new Set<string>();
  for (const c of showClauses) {
    const r = safeResolve(c.selector, undefined, c.source);
    if (!r) continue;
    if (r.sort === "node") for (const k of r.nodes) showKeys.add(k);
    else for (const e of r.edges) addEdgeEndpoints(e, showKeys); // edge-sort show ⇒ its endpoints
  }
  shown = showClauses.length > 0 ? showKeys : new Set(allKeys);

  // hide: node-sort subtracts membership; edge-sort drops those edges.
  for (const c of effective.clauses) {
    if (c.kind !== "hide") continue;
    const r = safeResolve(c.selector, shown, c.source);
    if (!r) continue;
    if (r.sort === "node") for (const k of r.nodes) hidden.add(k);
    else for (const e of r.edges) if (isCallEdge(e)) edgeDrop.add(edgeIdOf(e));
  }
  for (const k of hidden) shown.delete(k);

  // on <plane>: narrow membership to elements present on the target plane(s) (S16).
  // `on a, b` (weave) = union of the planes; an element with no explicit plane is on
  // `primary` only, so a non-primary target makes it absent. Membership only — the
  // weave lane LAYOUT is the board's concern (deferred). A member carries its OWN
  // presence when a partial-presence block gave it one (§1.2 — a plane-exclusive
  // interface); otherwise it follows its owning module (children inherit §1.2).
  const onDiags: Diagnostic[] = [];
  const onClauses = effective.clauses.filter((c) => c.kind === "on");
  const targetPlanes = new Set<string>();
  if (onClauses.length > 0) {
    for (const c of onClauses) if (c.kind === "on") for (const p of c.planes) targetPlanes.add(p);
    const presence = ctx.presence;
    if (!presence) {
      onDiags.push(makeDiag("warning", effective.source, "VIEW_ON_NO_PRESENCE", "view.onNoPresence"));
    } else {
      for (const p of targetPlanes) {
        if (p !== PRIMARY_PLANE && !presence.planes.includes(p)) {
          onDiags.push(makeDiag("warning", effective.source, "VIEW_UNKNOWN_PLANE", "view.unknownPlane", { plane: p }));
        }
      }
      for (const key of [...shown]) {
        const planesOfM =
          presence.planesOf.get(key) ?? presence.planesOf.get(owningModuleKey(key, u)) ?? PRIMARY_ONLY;
        let present = false;
        for (const tp of targetPlanes) if (planesOfM.has(tp)) { present = true; break; }
        if (!present) shown.delete(key);
      }
    }
  }
  // The DEFAULT plane, for a view that named none: hide what a partial-presence
  // block declared, and nothing else. A block-declared node takes its subtree
  // with it (children inherit its planes); a block-declared MEMBER is not a
  // universe node at all, so it travels to the renderer in `planeHiddenMembers`.
  const planeHiddenMembers = new Set<string>();
  if (defaultPlaneNarrows) {
    const exclusive = ctx.presence!.planeExclusiveMembers;
    for (const key of exclusive) planeHiddenMembers.add(key);
    const hiddenByBlock = (key: string): boolean => {
      const bare = key.includes("::") ? key.slice(key.lastIndexOf("::") + 2) : key;
      if (exclusive.has(bare)) return true;
      let cut = bare.lastIndexOf(".");
      while (cut > 0) {
        if (exclusive.has(bare.slice(0, cut))) return true;
        cut = bare.lastIndexOf(".", cut - 1);
      }
      return false;
    };
    for (const key of [...shown]) {
      if (hiddenByBlock(key)) shown.delete(key);
    }
  }

  // Click-reveal (VIEWS §7): exact-key union, after hide AND `on` — the
  // plane pass would otherwise delete a revealed off-plane peer, and a
  // synthesized `show` clause would flip a show-less view's default-all
  // selection. A revealed key behaves like any shown node downstream
  // (groups, lifts, boundary, styles).
  if (revealedKeys) {
    for (const k of revealedKeys) {
      if (hidden.has(k)) continue;
      if (!allKeys.has(k)) continue;
      shown.add(k);
    }
  }

  // focus: emphasis, never membership (VIEWS §2). A node-sort clause marks the
  // matches that survived to `shown`; an EDGE-sort clause marks the wire itself
  // AND both its endpoints — the mirror of edge-sort `show` (`:395` above),
  // which is what "`focus A > B` means that call" has to mean when the call is
  // drawn between two boxes. Previously the edge sort was dropped on the floor
  // with no `else` and no diagnostic.
  const focusMark = (k: string): string | null => boardNodeKeyFor(k, shown, u);
  for (const c of effective.clauses) {
    if (c.kind !== "focus") continue;
    const r = safeResolve(c.selector, shown, c.source);
    if (!r) continue;
    let matched = 0;
    if (r.sort === "node") {
      matched = r.nodes.size;
      for (const k of r.nodes) {
        const mark = focusMark(k);
        if (mark) focus.add(mark);
      }
    } else {
      matched = r.edges.size;
      for (const e of r.edges) {
        if (!isCallEdge(e)) continue;
        const ends = new Set<string>();
        addEdgeEndpoints(e, ends);
        // Endpoints narrow to board nodes exactly like a node-sort match does;
        // the WIRE needs both ends, since there is nothing to draw between a
        // box and an absence.
        for (const k of ends) {
          const mark = focusMark(k);
          if (mark) focus.add(mark);
        }
        if ([...ends].every((k) => focusMark(k) !== null)) focusEdges.add(edgeIdOf(e));
      }
    }
    // Resolving to the EMPTY set is an authoring mistake — the clause names
    // nothing in the model at all. Resolving to something this view happens not
    // to show is NOT: that is ordinary composition (a parent's `focus`
    // inherited into an instance whose knob narrowed `show`, VIEWS §9), so it
    // stays silent. The two are distinguishable here because `resolveSet`
    // evaluates over the whole universe — `shown` is only the basis for
    // `outside` — and the narrowing is the `shown.has` filter just above.
    if (matched === 0) {
      selDiags.push(makeDiag("warning", c.source, VIEW_EMPTY_FOCUS, "view.emptyFocus"));
    }
  }

  // --- 1c. `group by in <sel>` — resolve the CONTAINERS ----------------------
  // Containment grouping needs its container set BEFORE `shown` is frozen: a
  // container that anchors a group becomes the frame, so it stops rendering as
  // a node (`show A or in A` + `group by in A` must not draw A twice, once as a
  // box and once as the frame around its own contents).
  const scopeMembers = new Map<GroupByClause, Map<string, Set<string>>>();
  for (const c of effective.clauses) {
    if (c.kind !== "groupBy" || c.scope === undefined) continue;
    const r = safeResolve(c.scope, shown, c.source);
    const byContainer = new Map<string, Set<string>>();
    scopeMembers.set(c, byContainer);
    if (!r) continue;
    if (r.sort !== "node") {
      selDiags.push(
        makeDiag("warning", c.source, VIEW_SELECTOR_ERROR, "view.groupByGetterError", {
          detail: "`group by in` needs a node-sort selector naming the containers",
        }),
      );
      continue;
    }
    if (r.nodes.size === 0) {
      selDiags.push(
        makeDiag("warning", c.source, VIEW_SELECTOR_ERROR, "view.groupByGetterError", {
          detail: "`group by in` names no container in this model",
        }),
      );
      continue;
    }
    let unevaluable = 0;
    for (const key of [...r.nodes].sort()) {
      const container = u.byKey(key);
      if (!container) continue;
      // An explicitly hidden container frames nothing — `hide A` beside
      // `group by in A` must not resurrect A as a frame.
      if (hidden.has(key)) continue;
      let members: Set<string>;
      try {
        members = containmentMembers(container, ctx);
      } catch (e) {
        // A kind nothing can be `in` (a test, a process with no derived graph)
        // is skipped with one clause-level warning — every other selector
        // failure in this planner degrades, and a broad `group by in *` must
        // not take down the whole projection (the headless render paths have
        // no catch of their own).
        if (!(e instanceof UnsupportedSelectorError) && !(e instanceof SortMismatchError)) throw e;
        unevaluable++;
        continue;
      }
      // A container becomes a FRAME only where it actually collects a shown
      // module. One that collects nothing stays an ordinary node — erasing it
      // would delete `show *`'s own selection (every leaf module matches
      // `group by in module`).
      if (![...members].some((k) => k !== key && shown.has(k) && u.byKey(k)?.structuralKind === "module")) continue;
      byContainer.set(key, members);
      // Out of `shown` AND into `hidden`: the frame replaces the node, so the
      // engine drops/lifts its edges explicitly instead of leaving a key that
      // is neither shown nor hidden — which `computeBoundary` would read as
      // "outside the view" and stub.
      shown.delete(key);
      hidden.add(key);
      // …and out of `focus` with it. The focus pass above narrows its marks by
      // `shown` — a mark on something this view does not draw is silently
      // dropped (VIEWS §9) — but it runs BEFORE this block, so a container that
      // only becomes a frame HERE would keep a stale mark. A frame has no board
      // node, so that mark matches nothing downstream and every item resolves
      // `ground`: the whole board goes under the veil.
      focus.delete(key);
    }
    if (unevaluable > 0) {
      selDiags.push(
        makeDiag("warning", c.source, VIEW_SELECTOR_ERROR, "view.groupByGetterError", {
          detail: `\`group by in\` matched ${unevaluable} element(s) nothing can be inside`,
        }),
      );
    }
  }

  // --- StableId bridge (BLOCKER-1) — built early so synthesized edges can stamp
  //     each endpoint's id-first key (matching the scene module's `moduleKey`). ---
  const stableIds = new Map<string, StableId>();
  if (ctx.graph) {
    for (const [m, path] of ctx.graph.pathOfModule) {
      const key = path.join(".");
      if (m.id && shown.has(key)) stableIds.set(key, m.id);
    }
  }

  // --- 2. hide mid-tree re-parent + via-edge lifting -------------------------
  const parentOf = new Map<string, string | null>();
  for (const n of u.nodes) {
    if (n.structuralKind !== "module" || !shown.has(n.key)) continue;
    parentOf.set(n.key, nearestShownModuleAncestor(n.path, shown, u));
  }

  // Drop every call edge incident to a hidden module; lift A→B around them.
  for (const e of callEdges) {
    const from = e.fromModulePath.join(".");
    const to = e.toModulePath.join(".");
    if (hidden.has(from) || hidden.has(to)) edgeDrop.add(edgeIdOf(e));
  }
  // BUG A dedup set: the logical (from,to,iface) identity of every real call edge
  // that SURVIVES the hide stage. A via-lift onto an identity a real edge still
  // carries would double-render that arrow, so `computeViaLifts` suppresses it.
  const keptIdentities = new Set<string>();
  for (const e of callEdges) {
    if (edgeDrop.has(edgeIdOf(e))) continue;
    keptIdentities.add(liftIdentity(e.fromModulePath.join("."), e.toModulePath.join("."), e.toInterfacePath.join(".")));
  }
  // Via-lifts as LOGICAL from→to pairs; the copy fan-out (§3b) turns each into the
  // final board edge(s) so a lift out of / into a copied origin fans to its copies.
  const liftPairs = computeViaLifts(callEdges, shown, hidden, keptIdentities);

  // --- 3. group by <getter> --------------------------------------------------
  const groupByClauses = effective.clauses.filter((c) => c.kind === "groupBy");
  const groups: GroupAssignment[] = [];
  const ungrouped = new Set<string>();
  const copyOrigin = new Map<string, string>();
  const copiesByOrigin = new Map<string, string[]>();
  if (groupByClauses.length > 0) {
    // Nesting by repetition (VIEWS §4): clause k partitions what clause k-1
    // grouped. Items flow through levels as (originKey, node, copied)
    // tuples — values always evaluate on the ORIGIN node, and a copy id is
    // minted only at LANDING from the final group path (`g<path>::<key>`),
    // so intermediate ids never exist and every map holds landing copies
    // only. A valueless item lands where it is: the current parent group's
    // DIRECT member (or the root pool at level 0).
    interface GroupItem {
      readonly originKey: string;
      readonly node: ElementNode;
      readonly copied: boolean;
    }
    const land = (g: GroupAssignment, path: string, item: GroupItem): void => {
      if (item.copied) {
        const copyId = `g${path}::${item.originKey}`;
        (g.copies as { copyId: string; originKey: string }[]).push({
          copyId,
          originKey: item.originKey,
        });
        copyOrigin.set(copyId, item.originKey);
        const list = copiesByOrigin.get(item.originKey) ?? [];
        list.push(copyId);
        copiesByOrigin.set(item.originKey, list);
      } else {
        (g.memberKeys as string[]).push(item.originKey);
      }
    };
    // A getter the evaluator can't run (deferred builtin, capability getter)
    // degrades that LEVEL to the ungrouped pool with ONE diagnostic — the
    // same non-fatal posture every other clause takes (a throw here would
    // take down the whole projection).
    const failedLevels = new Set<number>();
    const assignLevel = (
      items: readonly GroupItem[],
      level: number,
      parent: { g: GroupAssignment; path: string } | undefined,
    ): void => {
      const clause = groupByClauses[level];
      if (clause === undefined) {
        // No more levels: everything lands in the parent (leaf) group.
        for (const item of items) land(parent!.g, parent!.path, item);
        return;
      }
      const valuesByItem = new Map<GroupItem, string[]>();
      const distinct = new Set<string>();
      for (const item of items) {
        let vals: string[];
        if (failedLevels.has(level)) {
          vals = [];
        } else if (clause.scope !== undefined) {
          // Containment grouping: the container keys this item sits inside.
          // MULTI-valued by construction — an item inside two matched containers
          // (`in (A or A.Core)`) duplicates into both, exactly as a multi-valued
          // `@@aspect` does. A view is a projection, not a move (VIEWS §4).
          vals = [];
          for (const [key, members] of scopeMembers.get(clause) ?? []) {
            if (members.has(item.originKey)) vals.push(key);
          }
        } else {
          try {
            vals = groupValuesOf(clause.getter!, item.node, ctx);
          } catch (e) {
            if (!(e instanceof UnsupportedExprError)) throw e;
            failedLevels.add(level);
            selDiags.push(
              makeDiag("warning", clause.source, VIEW_SELECTOR_ERROR, "view.groupByGetterError", { detail: e.message }),
            );
            vals = [];
          }
        }
        valuesByItem.set(item, vals);
        for (const v of vals) distinct.add(v);
      }
      const sortedValues = [...distinct].sort();
      const indexOf = new Map(sortedValues.map((v, i) => [v, i + 1]));
      // Reserved `::`-scheme id (like copy ids) so a synthetic group can
      // NEVER shadow a real module whose qualified key is literally `g1`
      // (BUG C). Nested levels extend the path: `::group::1.2`.
      const scoped: { g: GroupAssignment; path: string; items: GroupItem[] }[] =
        sortedValues.map((v) => {
          const path = parent ? `${parent.path}.${indexOf.get(v)}` : `${indexOf.get(v)}`;
          return {
            g: {
              containerId: `::group::${path}`,
              label: v,
              memberKeys: [] as string[],
              copies: [] as { copyId: string; originKey: string }[],
              ...(parent ? { parentId: parent.g.containerId } : {}),
            },
            path,
            items: [],
          };
        });
      // Deterministic item iteration (sorted origin keys; a copied item
      // follows its origin's slot — items arrive pre-sorted per level).
      const sortedItems = [...items].sort((a, b) =>
        a.originKey < b.originKey ? -1 : a.originKey > b.originKey ? 1 : 0,
      );
      for (const item of sortedItems) {
        const vals = valuesByItem.get(item)!;
        if (vals.length === 0) {
          // Valueless at this level: land HERE (parent pool / root pool).
          if (parent === undefined) ungrouped.add(item.originKey);
          else land(parent.g, parent.path, item);
        } else if (vals.length === 1) {
          scoped[indexOf.get(vals[0]!)! - 1]!.items.push(item);
        } else {
          for (const v of vals) {
            scoped[indexOf.get(v)! - 1]!.items.push({ ...item, copied: true });
          }
        }
      }
      // Depth-first emission: each group, then its children.
      for (const sc of scoped) {
        groups.push(sc.g);
        assignLevel(sc.items, level + 1, { g: sc.g, path: sc.path });
      }
    };
    const rootItems: GroupItem[] = u.nodes
      .filter((n) => n.structuralKind === "module" && shown.has(n.key))
      .map((n) => ({ originKey: n.key, node: n, copied: false }));
    assignLevel(rootItems, 0, undefined);
  }

  // --- 3b. edge fan-out over per-group copies (Major-6 + BUG B) --------------
  // Both via-lifts AND real edges route through the SAME copy fan-out: whenever an
  // endpoint became a per-group copy the edge is emitted once per (copy-of-from →
  // copy-of-to) pair, carrying the copy id as its id-first key. A lift out of / into
  // a copied origin therefore attaches to every copy instead of the un-rendered
  // origin key (BUG B), preserving its "via [H…]" attribution. Edges with no copied
  // endpoint pass straight through — real ones stay in the scene edge list, lifts
  // are emitted once via `edgeLift.add`.
  const addedEdges: LiftedEdge[] = [];
  for (const lp of liftPairs) {
    addedEdges.push(...fanOutEdge(lp, "via-lift", copiesByOrigin, stableIds));
  }
  if (copiesByOrigin.size > 0) {
    for (const e of callEdges) {
      const from = e.fromModulePath.join(".");
      const to = e.toModulePath.join(".");
      if (!shown.has(from) || !shown.has(to)) continue;
      if (!copiesByOrigin.has(from) && !copiesByOrigin.has(to)) continue; // neither copied
      edgeDrop.add(edgeIdOf(e));
      addedEdges.push(
        ...fanOutEdge(
          { fromKey: from, toKey: to, toInterfaceKey: e.toInterfacePath.join("."), via: [] },
          "group-copy",
          copiesByOrigin,
          stableIds,
          e,
        ),
      );
    }
  }

  // --- 4. boundary stubs -----------------------------------------------------
  const boundary = computeBoundary(
    callEdges,
    shown,
    hidden,
    boundaryHideRules(effective.clauses, ctx, shown, selDiags),
  );

  // --- 5. styles (node color/overlay + edge overlays + legend + casts);
  //        `outside` resolves vs the final shown set. ---
  const style = evalStyleClauses(effective, ctx, shown);

  const atClause = effective.clauses.find((c): c is AtClause => c.kind === "at");

  // Boundary-stub restyle (VIEWS §7): merge matching rules onto the marks,
  // clause order — later rules win per property.
  const styledBoundary =
    style.boundaryStyleRules.length === 0
      ? boundary
      : boundary.map((mark) => ({
          ...mark,
          stubs: mark.stubs.map((stub) => {
            let merged: EdgeStyle | undefined = stub.style;
            for (const rule of style.boundaryStyleRules) {
              if (!rule.nodes.has(mark.nodeKey)) continue;
              if (rule.direction !== "both" && rule.direction !== stub.direction) continue;
              merged = { ...merged, ...rule.style };
            }
            return merged ? { ...stub, style: merged } : stub;
          }),
        }));

  return {
    shown,
    hidden,
    focus,
    focusEdges,
    planes: targetPlanes,
    planeHiddenMembers,
    ...(atClause ? { at: atClause } : {}),
    parentOf,
    groups,
    ungrouped,
    copyOrigin,
    edgeLift: { drop: edgeDrop, add: [...addedEdges].sort((a, b) => a.edgeId.localeCompare(b.edgeId)) },
    styleIndex: style.styleIndex,
    edgeStyleIndex: style.edgeStyleIndex,
    boundary: styledBoundary,
    stableIds,
    colorResult: style.colorResult,
    legend: style.legend,
    pins: style.pins,
    renderModeByAspect: style.renderModeByAspect,
    diagnostics: [...instanceDiags, ...knobResult.diagnostics, ...selDiags, ...onDiags, ...style.diagnostics],
  };
}

// =============================================================================
// Knob bindings + view-instance merge (§3, S18)
// =============================================================================

/** A clause selector threw during projection (unbound / wrong knob, deferred atom). */
export const VIEW_SELECTOR_ERROR = "VIEW_SELECTOR_ERROR";
/** An `in <Ref>` names nothing in scope (unknown, or an ambiguous short name). */
export const VIEW_UNKNOWN_IN_TARGET = "VIEW_UNKNOWN_IN_TARGET";
/**
 * A `focus` selector matched nothing ANYWHERE in the model — a view that asks
 * for emphasis and gets none. Deliberately not raised for a selector that
 * resolves but matches nothing this view shows: that is composition, not a
 * mistake (VIEWS §9).
 */
export const VIEW_EMPTY_FOCUS = "VIEW_EMPTY_FOCUS";

/** The kinds something can be `in` — the containable half of
 *  {@link containmentMembers}'s switch. An interface is NOT one: it is a leaf,
 *  so `in <endpoint>` is blank by construction and worth saying so. */
const CONTAINER_KINDS: ReadonlySet<string> = new Set([
  "module",
  "surface",
  "space",
  "process",
  "subprocess",
]);

/** Why a written `in <Ref>` yields a blank view. */
interface BadInTarget {
  readonly path: QualifiedName;
  /** `unknown` — the name binds to nothing; `uncontainable` — it binds, but to
   *  nothing that can hold elements inside it (`in endpoint`, or a kind with no
   *  instances). Both render an empty selection, so both are worth saying. */
  readonly reason: "unknown" | "uncontainable";
}

/**
 * Every `in <Ref>` inside a selector that cannot produce anything — using the
 * evaluator's own resolution rule, so the lint and `evalIn` never disagree.
 * `in this` carries no ref and is skipped (a view clause has no bound subject,
 * so it is already reported as a deferred node).
 */
function unresolvedInTargets(sel: Selector, ctx: EvalContext): readonly BadInTarget[] {
  const out: BadInTarget[] = [];
  const walk = (s: Selector): void => {
    switch (s.kind) {
      case "in": {
        if (s.arg.kind !== "ref") {
          walk(s.arg);
          return;
        }
        // `refNamesSomething`, not `resolveScopedRef`: `in service` names a
        // KIND and selects every service, so a bound name is not a mistake.
        if (!refNamesSomething(s.arg.path, ctx)) {
          out.push({ path: s.arg.path, reason: "unknown" });
          return;
        }
        const reason = uncontainable(s.arg, ctx);
        if (reason) out.push({ path: s.arg.path, reason });
        return;
      }
      case "and":
      case "or":
        for (const t of s.terms) walk(t);
        return;
      case "not":
        walk(s.term);
        return;
      case "extraction":
        walk(s.inner);
        return;
      case "arrow":
        walk(s.head);
        for (const h of s.hops) walk(h.rhs);
        return;
      default:
        return;
    }
  };
  walk(sel);
  return out;
}

/**
 * Does a bound `in` target name nothing that can HOLD anything? Two shapes, one
 * outcome (an empty selection nobody wrote on purpose): the name matches no
 * element at all (a kind with no instances — `in subprocess`), or every element
 * it matches is of a kind nothing can be inside (`in endpoint`). The predicate
 * itself degrades silently over those, so the warning has to come from here.
 */
function uncontainable(arg: Selector, ctx: EvalContext): "uncontainable" | null {
  let containers: ReadonlySet<string>;
  try {
    const r = evalSelector(arg, ctx);
    if (r.sort !== "node") return null;
    containers = r.nodes;
  } catch {
    return null; // a real evaluation error is `safeResolve`'s to report
  }
  for (const key of containers) {
    const kind = ctx.universe.byKey(key)?.structuralKind;
    if (kind !== undefined && CONTAINER_KINDS.has(kind)) return null;
  }
  return "uncontainable";
}

/** A knob value has the wrong type / element kind for its declaration. */
export const VIEW_KNOB_TYPE = "VIEW_KNOB_TYPE";
/** A numeric knob value is outside its declared min/max/step. */
export const VIEW_KNOB_RANGE = "VIEW_KNOB_RANGE";
/** A `from` / enum knob value is not in the declared/allowed value set. */
export const VIEW_KNOB_SET = "VIEW_KNOB_SET";
/** A binding names a knob the (parent) view never declared. */
export const VIEW_UNKNOWN_KNOB = "VIEW_UNKNOWN_KNOB";
/** A view-instance references a parent view that is not in scope. */
export const VIEW_UNKNOWN_PARENT = "VIEW_UNKNOWN_PARENT";
/** The merged instance clause set has more than one representation clause. */
export const VIEW_MULTIPLE_REPRESENTATIONS = "VIEW_MULTIPLE_REPRESENTATIONS";

const REPRESENTATION_KINDS: ReadonlySet<ViewClause["kind"]> = new Set(["table", "matrix", "grid", "flow"]);

/**
 * Resolve a view's knob values into the `ctx.knobs` map the evaluator reads
 * (`$knob` atoms, `@@key:$knob`, `within $knob`). For each declared knob: take its
 * default (number knobs carry one), then override with the view/instance binding of
 * the same name, then kind-check the value against the declaration. A binding that
 * names no declared knob, or a value that fails its knob's type / range / value-set
 * constraint, is a targeted diagnostic. Soft violations (out-of-range number,
 * unresolved element, not-in-set) still bind the value so the view projects; a hard
 * type error (a non-number for a numeric knob) is withheld — it would crash `within`.
 */
export function resolveKnobBindings(
  view: ViewDeclV2,
  universe: EvalContext["universe"]
): { knobs: Map<string, Value>; diagnostics: Diagnostic[] } {
  const knobs = new Map<string, Value>();
  const diagnostics: Diagnostic[] = [];

  const declByName = new Map<string, KnobDecl>();
  for (const d of view.header.knobs) declByName.set(d.name, d);

  const bindingByName = new Map<string, KnobBinding>();
  for (const b of view.bindings ?? []) {
    bindingByName.set(b.name, b);
    if (!declByName.has(b.name)) {
      diagnostics.push(
        makeDiag("warning", b.nameSource ?? b.source, VIEW_UNKNOWN_KNOB, "view.unknownKnob", {
          name: b.name,
          view: view.instanceOf ?? view.name,
        }),
      );
    }
  }

  for (const decl of view.header.knobs) {
    const binding = bindingByName.get(decl.name);
    const value = binding?.value ?? defaultOfKnob(decl);
    if (value === undefined) continue; // unbound (no default, no binding) — a `$knob` use errors at eval
    const source = binding?.source ?? decl.source;
    const problem = checkKnobValue(decl, value, universe);
    if (problem) {
      diagnostics.push(makeDiag("warning", source, problem.code, problem.msgKey, problem.params));
      if (problem.withhold) continue;
    }
    knobs.set(decl.name, value);
  }

  return { knobs, diagnostics };
}

/** The default value a knob declaration carries (only the numeric shape does). */
function defaultOfKnob(decl: KnobDecl): Value | undefined {
  return decl.kind === "number" ? decl.default : undefined;
}

interface KnobProblem {
  readonly code: string;
  readonly msgKey: string;
  readonly params?: Readonly<Record<string, unknown>>;
  /** Withhold the binding entirely (the value would crash the evaluator). */
  readonly withhold?: boolean;
}

/** Kind-check a resolved knob value against its declaration (§3). */
function checkKnobValue(decl: KnobDecl, value: Value, universe: EvalContext["universe"]): KnobProblem | undefined {
  switch (decl.kind) {
    case "number": {
      if (value.kind !== "number") {
        return { code: VIEW_KNOB_TYPE, msgKey: "view.knobTypeNumber", params: { name: decl.name, got: value.kind }, withhold: true };
      }
      const n = value.value;
      if (decl.min !== undefined && n < decl.min) return { code: VIEW_KNOB_RANGE, msgKey: "view.knobRangeMin", params: { name: decl.name, value: n, min: decl.min } };
      if (decl.max !== undefined && n > decl.max) return { code: VIEW_KNOB_RANGE, msgKey: "view.knobRangeMax", params: { name: decl.name, value: n, max: decl.max } };
      if (decl.step !== undefined && decl.step > 0) {
        const base = decl.min ?? 0;
        const steps = (n - base) / decl.step;
        if (Math.abs(steps - Math.round(steps)) > 1e-9) {
          return { code: VIEW_KNOB_RANGE, msgKey: "view.knobRangeStep", params: { name: decl.name, value: n, step: decl.step, base } };
        }
      }
      return undefined;
    }
    case "element": {
      const path = value.kind === "ref" ? value.path : value.kind === "identifier" ? [value.text] : undefined;
      if (!path) return { code: VIEW_KNOB_TYPE, msgKey: "view.knobTypeElementRef", params: { name: decl.name, got: value.kind } };
      const el = universe.resolveRef([...path]);
      if (!el) return { code: VIEW_KNOB_TYPE, msgKey: "view.knobTypeNoElement", params: { name: decl.name, path: path.join(".") } };
      const wantsStructuralKind = BUILTIN_KINDS.has(decl.elementType);
      const ok = wantsStructuralKind ? el.structuralKind === decl.elementType : el.typeChain.includes(decl.elementType);
      if (!ok) return { code: VIEW_KNOB_TYPE, msgKey: "view.knobTypeNotElementType", params: { name: decl.name, path: path.join("."), elementType: decl.elementType } };
      return undefined;
    }
    case "from": {
      const allowed = allowedFromValues(decl.getter, universe);
      const items = value.kind === "list" ? value.items : [value];
      for (const it of items) {
        const t = knobValueText(it);
        if (t === undefined || !allowed.has(t)) {
          return { code: VIEW_KNOB_SET, msgKey: "view.knobSetNotFrom", params: { name: decl.name, value: t ?? it.kind, getter: `${decl.getter.sigil}${decl.getter.path.join(".")}` } };
        }
      }
      return undefined;
    }
    case "enum": {
      const allowed = new Set(decl.values.map(knobValueText).filter((s): s is string => s !== undefined));
      const items = value.kind === "list" ? value.items : [value];
      if (decl.select === "one" && items.length > 1) {
        return { code: VIEW_KNOB_SET, msgKey: "view.knobSetOneOf", params: { name: decl.name } };
      }
      for (const it of items) {
        const t = knobValueText(it);
        if (t === undefined || !allowed.has(t)) {
          return { code: VIEW_KNOB_SET, msgKey: "view.knobSetNotEnum", params: { name: decl.name, value: t ?? it.kind, allowed: `{${[...allowed].join(", ")}}` } };
        }
      }
      return undefined;
    }
  }
}

/** The distinct value texts a `from <getter>` knob may select — the aspect (`@@`) /
 *  field (`@`) values carried across the universe. */
function allowedFromValues(getter: ExprGetter, universe: EvalContext["universe"]): Set<string> {
  const key = getter.path.join(".");
  const out = new Set<string>();
  for (const n of universe.nodes) {
    if (getter.sigil === "@@") {
      for (const v of n.aspects.get(key) ?? []) {
        const t = knobValueText(v);
        if (t !== undefined) out.add(t);
      }
    } else {
      const v = n.fields.get(key);
      const t = v ? knobValueText(v) : undefined;
      if (t !== undefined) out.add(t);
    }
  }
  return out;
}

/** A model `Value` → its comparable text (for knob value-set membership). */
function knobValueText(v: Value): string | undefined {
  switch (v.kind) {
    case "string":
    case "identifier":
      return v.text;
    case "ref":
      return v.path.join(".");
    case "number":
      return String(v.value);
    case "boolean":
      return String(v.value);
    case "list":
    case "expr":
      // Neither a list nor an unresolved expression is a knob value.
      return undefined;
  }
}

/**
 * Resolve a view-instance (`view <Parent> <Name> { <bindings> <clauses> }`) into its
 * effective merged view: the PARENT view's clauses followed by the instance's own
 * appended clauses, carrying the parent's knob DECLS + the instance's name / id /
 * bindings. A non-instance view passes through unchanged. An unknown parent yields
 * the instance's own clauses (still renderable) plus a targeted diagnostic.
 * Representation-exclusivity is re-checked over the MERGED clause set.
 */
export function resolveViewInstance(
  view: ViewDeclV2,
  viewsByName?: ReadonlyMap<string, ViewDeclV2>
): { view: ViewDeclV2; diagnostics: Diagnostic[] } {
  if (!view.instanceOf) return { view, diagnostics: [] };
  const diagnostics: Diagnostic[] = [];
  const parent = viewsByName?.get(view.instanceOf);
  if (!parent) {
    diagnostics.push(
      makeDiag("warning", view.parentSource ?? view.nameSource ?? view.source, VIEW_UNKNOWN_PARENT, "view.unknownParent", {
        parent: view.instanceOf,
        instance: view.name,
      }),
    );
    return { view, diagnostics };
  }

  const clauses = [...parent.clauses, ...view.clauses];
  const reps = clauses.filter((c) => REPRESENTATION_KINDS.has(c.kind));
  if (reps.length > 1) {
    diagnostics.push(
      makeDiag("warning", view.source, VIEW_MULTIPLE_REPRESENTATIONS, "view.multipleRepresentations", {
        instance: view.name,
        count: reps.length,
        parent: parent.name,
        kinds: reps.map((c) => c.kind).join(", "),
      }),
    );
  }

  const description = view.description ?? parent.description;
  const merged: ViewDeclV2 = {
    ...parent,
    id: view.id ?? parent.id,
    name: view.name,
    nameSource: view.nameSource,
    ...(description !== undefined ? { description } : {}),
    header: parent.header, // knob DECLS from the parent; the instance BINDS them
    clauses,
    instanceOf: view.instanceOf,
    bindings: view.bindings ?? [],
    ownerPath: view.ownerPath ?? parent.ownerPath,
    source: view.source,
  };
  return { view: merged, diagnostics };
}

// =============================================================================
// Sub-step helpers
// =============================================================================

type SetResult =
  | { readonly sort: "node"; readonly nodes: Set<string> }
  | { readonly sort: "edge"; readonly edges: Set<DependencyEdge> };

/**
 * Evaluate a selector to its node/edge set, resolving `outside` (which the core
 * `evalSelector` defers) against the FINAL `shown` set. Node-sort `outside`
 * compositions (`outside`, `outside and X`, `not outside`, …) are handled here;
 * every other atom delegates to `evalSelector`.
 */
function resolveSet(sel: Selector, ctx: EvalContext, shown: Set<string> | undefined): SetResult {
  if (mentionsOutside(sel)) {
    // Node-sort `outside` compositions (`outside`, `outside and X`, `not outside`)
    // resolve to the complement of the final shown set. Edge-side `outside` inside
    // an arrow (`* <> outside`) is closure-shaped and DEFERRED — return an empty
    // edge set so the clause is a no-op rather than crashing `evalSelector`.
    try {
      const all = new Set(ctx.universe.nodes.map((n) => n.key));
      return { sort: "node", nodes: evalNodeWithOutside(sel, ctx, shown ?? new Set(), all) };
    } catch {
      return { sort: "edge", edges: new Set() };
    }
  }
  const r = evalSelector(sel, ctx);
  return r.sort === "node"
    ? { sort: "node", nodes: r.nodes }
    : { sort: "edge", edges: r.edges as Set<DependencyEdge> };
}

/** Does `outside` appear anywhere in the selector tree (any sort)? Full recursion
 *  so an arrow-embedded `outside` (`* <> outside`) is caught and routed to the
 *  deferred path, not left to throw in `evalSelector`. */
function mentionsOutside(sel: Selector): boolean {
  switch (sel.kind) {
    case "outside":
      return true;
    case "and":
    case "or":
      return sel.terms.some(mentionsOutside);
    case "not":
      return mentionsOutside(sel.term);
    case "in":
      return mentionsOutside(sel.arg);
    case "extraction":
      return mentionsOutside(sel.inner);
    case "arrow":
      return mentionsOutside(sel.head) || sel.hops.some((h) => mentionsOutside(h.rhs));
    default:
      return false;
  }
}

/** Node-sort evaluation with `outside` = (all \ shown). Only the boolean
 *  combinators + `outside` are resolved here; leaves defer to `evalSelector`. */
function evalNodeWithOutside(sel: Selector, ctx: EvalContext, shown: Set<string>, all: Set<string>): Set<string> {
  switch (sel.kind) {
    case "outside":
      return new Set([...all].filter((k) => !shown.has(k)));
    case "and": {
      const parts = sel.terms.map((t) => evalNodeWithOutside(t, ctx, shown, all));
      return parts.reduce((acc, s) => new Set([...acc].filter((k) => s.has(k))));
    }
    case "or": {
      const out = new Set<string>();
      for (const t of sel.terms) for (const k of evalNodeWithOutside(t, ctx, shown, all)) out.add(k);
      return out;
    }
    case "not": {
      const inner = evalNodeWithOutside(sel.term, ctx, shown, all);
      return new Set([...all].filter((k) => !inner.has(k)));
    }
    default: {
      const r = evalSelector(sel, ctx);
      if (r.sort !== "node") throw new Error("`outside` composes only with node-sort terms");
      return r.nodes;
    }
  }
}

/**
 * The board node a projection key marks, or `null` for one that draws nothing.
 *
 * `shown` is NOT "the nodes": it is every element key that survives the
 * projection, sub-elements included. Only a MODULE becomes a box — the same
 * `structuralKind === "module"` filter the parent-chain and group passes apply
 * — so a mark on any other kind names a key no board id can ever match.
 *
 * For `focus` that distinction is load-bearing rather than cosmetic. The
 * resolvers mark the matches figure and everything ELSE ground, so a single
 * unmatchable key does not merely fail to emphasise: it veils the whole board,
 * inverting emphasis into erasure. `group by in` was one route in (a container
 * leaving `shown` after the focus pass, closed at its own site); the KIND of
 * the match is the other, and `focus <interface>` reaches it with no clause at
 * all.
 *
 * An interface/surface widens to its owning module instead of dropping, which
 * is what an edge-sort `focus` already does with the very same names
 * (`focus Orders > Payments.Charge` marks the two boxes) — and the board has no
 * per-member emphasis channel for it to mean anything else. A process or a
 * test has neither a box nor a box to widen to, and drops.
 *
 * A `space` is NOT one of the droppers, however it reads: it has no structural
 * kind of its own, it builds a MODULE node that holds others, and it draws.
 */
function boardNodeKeyFor(
  key: string,
  shown: Set<string>,
  u: EvalContext["universe"]
): string | null {
  const n = u.byKey(key);
  if (!n) return null;
  if (n.structuralKind === "module") return shown.has(key) ? key : null;
  if (n.structuralKind !== "interface" && n.structuralKind !== "surface") return null;
  return nearestShownModuleAncestor(n.path, shown, u);
}

/** Nearest ancestor of `path` that is a shown module (`null` = board root). */
function nearestShownModuleAncestor(
  path: readonly string[],
  shown: Set<string>,
  u: EvalContext["universe"]
): string | null {
  for (let len = path.length - 1; len >= 1; len--) {
    const key = path.slice(0, len).join(".");
    if (u.byKey(key)?.structuralKind === "module" && shown.has(key)) return key;
  }
  return null;
}

/** A logical via-lift, BEFORE copy fan-out: an A→B routing around hidden nodes.
 *  {@link fanOutEdge} turns each pair into the final board `LiftedEdge`(s). */
interface LiftPair {
  readonly fromKey: string;
  readonly toKey: string;
  readonly toInterfaceKey: string;
  readonly via: readonly string[];
}

/** The logical identity of an edge/lift — its (from, to, toInterface) triple.
 *  Distinct from {@link edgeIdOf} (which also pins process/step): two arrows that
 *  differ only by process step collapse to one identity, matching board dedup. */
function liftIdentity(from: string, to: string, iface: string): string {
  return JSON.stringify([from, to, iface]);
}

/**
 * Fan one logical from→to edge over the copy fan-out. When an endpoint became a
 * per-group copy, emit one edge per (copy-of-from → copy-of-to) pair, stamping the
 * copy id as the id-first key; a non-copied endpoint keeps its StableId so its edge
 * key still matches the module's `moduleKey`. Real edges (`group-copy`) and via-lifts
 * (`via-lift`, attribution preserved) share this so a lift out of / into a copied
 * origin attaches to every copy, never the un-rendered origin key (BUG B).
 */
function fanOutEdge(
  pair: LiftPair,
  origin: "via-lift" | "group-copy",
  copiesByOrigin: ReadonlyMap<string, string[]>,
  stableIds: ReadonlyMap<string, StableId>,
  disamb?: { readonly processName?: string; readonly stepIndex?: number }
): LiftedEdge[] {
  const { fromKey, toKey, toInterfaceKey, via } = pair;
  const fromCopies = copiesByOrigin.get(fromKey);
  const toCopies = copiesByOrigin.get(toKey);
  const froms = fromCopies ?? [fromKey];
  const tos = toCopies ?? [toKey];
  // Both endpoints copied: pair each from-copy with the to-copies sharing
  // the DEEPEST common group-path ancestor (a raw cross product asserts
  // cross-group couplings the model never expresses — same-leaf beats
  // same-outer beats nothing); a from-copy sharing no ancestor with any
  // to-copy falls back to every to-copy (the call is a model fact — it
  // must render somewhere).
  const pathOf = (copyId: string): string[] => copyId.slice(0, copyId.indexOf("::")).split(".");
  const commonDepth = (a: string[], b: string[]): number => {
    let d = 0;
    while (d < a.length && d < b.length && a[d] === b[d]) d++;
    return d;
  };
  const toPaths = fromCopies && toCopies ? toCopies.map((c) => ({ c, p: pathOf(c) })) : null;
  const matesOf = (rf: string): readonly string[] => {
    if (!toPaths) return tos;
    const fp = pathOf(rf);
    let best = 0;
    for (const t of toPaths) best = Math.max(best, commonDepth(fp, t.p));
    if (best === 0) return tos;
    return toPaths.filter((t) => commonDepth(fp, t.p) === best).map((t) => t.c);
  };
  const out: LiftedEdge[] = [];
  for (const rf of froms) {
    for (const rt of matesOf(rf)) {
      out.push({
        origin,
        fromKey,
        toKey,
        toInterfaceKey,
        via: [...via],
        fromId: fromCopies ? rf : stableIds.get(fromKey),
        toId: toCopies ? rt : stableIds.get(toKey),
        edgeId:
          origin === "via-lift"
            ? JSON.stringify(["lift", rf, rt, toInterfaceKey])
            : JSON.stringify(["copy", rf, rt, toInterfaceKey, disamb?.processName ?? null, disamb?.stepIndex ?? null]),
      });
    }
  }
  return out;
}

/**
 * Via-lift: for every shown A with an edge into a hidden module, follow the hidden
 * intermediates (transitively) to each reachable shown B (B≠A) and record a logical
 * A→B pair attributed to the hidden path. Deduped by (from, to, toInterface); the
 * lexicographically smallest via-path wins for stable attribution. A lift whose
 * identity a surviving real edge already carries is suppressed (BUG A) — dedup lives
 * on the logical pair, BEFORE the copy fan-out that follows.
 */
function computeViaLifts(
  callEdges: readonly CallEdge[],
  shown: Set<string>,
  hidden: Set<string>,
  keptIdentities: ReadonlySet<string>
): LiftPair[] {
  const outAdj = new Map<string, { to: string; toIface: string }[]>();
  for (const e of callEdges) {
    const from = e.fromModulePath.join(".");
    const list = outAdj.get(from) ?? [];
    list.push({ to: e.toModulePath.join("."), toIface: e.toInterfacePath.join(".") });
    outAdj.set(from, list);
  }
  const best = new Map<string, LiftPair>();
  const consider = (from: string, to: string, toIface: string, via: string[]): void => {
    // BUG A: a lift onto an identity a surviving real edge already carries is a
    // visual duplicate — never emit it.
    if (keptIdentities.has(liftIdentity(from, to, toIface))) return;
    const key = JSON.stringify(["lift", from, to, toIface]);
    const viaStr = via.join(">");
    const prev = best.get(key);
    if (prev && prev.via.join(">") <= viaStr) return;
    best.set(key, { fromKey: from, toKey: to, toInterfaceKey: toIface, via: [...via] });
  };
  for (const a of [...shown].sort()) {
    for (const hop of outAdj.get(a) ?? []) {
      if (!hidden.has(hop.to)) continue;
      // DFS through hidden intermediates to shown targets.
      const walk = (cur: string, via: string[]): void => {
        for (const next of outAdj.get(cur) ?? []) {
          if (shown.has(next.to) && next.to !== a) {
            consider(a, next.to, next.toIface, via);
          } else if (hidden.has(next.to) && !via.includes(next.to)) {
            walk(next.to, [...via, next.to]);
          }
        }
      };
      walk(hop.to, [hop.to]);
    }
  }
  return [...best.values()];
}

/** A boundary-stub suppression rule derived from a `hide` clause whose
 *  single-hop arrow has a bare `outside` on exactly one side: the resolved
 *  shown-side node set + which stub direction it kills. */
interface BoundaryHideRule {
  readonly nodes: ReadonlySet<string>;
  readonly direction: "in" | "out" | "both";
}

/**
 * Collect boundary suppression rules from `hide` clauses (VIEWS §7):
 * `hide * <> outside` kills every stub, `hide X > outside` kills X's
 * outgoing stubs, `hide outside > X` kills X's incoming stubs. Only bare
 * `outside` on a side of a single `>`/`<>` hop qualifies — `>>` combined
 * with `outside` emits a diagnostic (undefined semantics) and is skipped.
 */
function boundaryHideRules(
  clauses: readonly ViewClause[],
  ctx: EvalContext,
  shown: Set<string>,
  diagnostics: Diagnostic[],
): BoundaryHideRule[] {
  const rules: BoundaryHideRule[] = [];
  for (const c of clauses) {
    if (c.kind !== "hide") continue;
    const sel = c.selector;
    if (sel.kind !== "arrow" || sel.hops.length !== 1) continue;
    const hop = sel.hops[0]!;
    const headOutside = sel.head.kind === "outside";
    const rhsOutside = hop.rhs.kind === "outside";
    if (hop.op === ">>") {
      if (headOutside !== rhsOutside) {
        diagnostics.push(
          makeDiag("warning", c.source, VIEW_SELECTOR_ERROR, "view.selectorError", {
            detail: "`>>` (transitive closure) combined with `outside` is not supported — use `>` or `<>` for boundary-stub rules",
          }),
        );
      }
      continue;
    }
    if (headOutside === rhsOutside) continue; // both or neither — not a stub rule
    const nodeSel = headOutside ? hop.rhs : sel.head;
    let nodes: ReadonlySet<string>;
    try {
      const r = evalSelector(nodeSel, ctx);
      if (r.sort !== "node") continue;
      nodes = r.nodes;
    } catch {
      continue;
    }
    const direction: BoundaryHideRule["direction"] =
      hop.op === "<>" ? "both" : headOutside ? "in" : "out";
    rules.push({ nodes: new Set([...nodes].filter((k) => shown.has(k))), direction });
  }
  return rules;
}

/** Boundary marks: a shown module with an edge to an absent (not-shown, not-hidden)
 *  module gets a port-nub per (far endpoint × direction), carrying the count of
 *  aggregated calls — minus whatever the `hide … outside` rules suppress. */
function computeBoundary(
  callEdges: readonly CallEdge[],
  shown: Set<string>,
  hidden: Set<string>,
  hideRules: readonly BoundaryHideRule[],
): BoundaryMark[] {
  const stubsByNode = new Map<string, Map<string, BoundaryStub>>();
  const mark = (node: string, peer: string, direction: "in" | "out"): void => {
    for (const rule of hideRules) {
      if (!rule.nodes.has(node)) continue;
      if (rule.direction === "both" || rule.direction === direction) return;
    }
    const stubs = stubsByNode.get(node) ?? new Map<string, BoundaryStub>();
    const key = `${peer}|${direction}`;
    const prev = stubs.get(key);
    stubs.set(key, { peerKey: peer, direction, count: (prev?.count ?? 0) + 1 });
    stubsByNode.set(node, stubs);
  };
  const isAbsent = (k: string): boolean => !shown.has(k) && !hidden.has(k);
  for (const e of callEdges) {
    const from = e.fromModulePath.join(".");
    const to = e.toModulePath.join(".");
    if (shown.has(from) && isAbsent(to)) mark(from, to, "out");
    else if (shown.has(to) && isAbsent(from)) mark(to, from, "in");
  }
  return [...stubsByNode.keys()].sort().map((nodeKey) => ({
    nodeKey,
    stubs: [...stubsByNode.get(nodeKey)!.values()].sort(
      (a, b) => a.peerKey.localeCompare(b.peerKey) || a.direction.localeCompare(b.direction),
    ),
  }));
}

/** Add a call edge's module endpoints (source + callee owner) to a key set. */
function addEdgeEndpoints(e: DependencyEdge, into: Set<string>): void {
  if (e.kind !== "call") return;
  into.add(e.fromModulePath.join("."));
  into.add(e.toModulePath.join("."));
}

// =============================================================================
// group by — value extraction (list-aware)
// =============================================================================

/**
 * The group values a node contributes under `<getter>`. An `@@aspect` getter is
 * LIST-aware (a module with several occurrences of the key joins multiple groups →
 * per-copy identity); `@field` / builtin getters yield at most one value. Missing
 * ⇒ `[]` (the ungrouped pool).
 */
function groupValuesOf(getter: ExprGetter, node: ElementNode, ctx: EvalContext): string[] {
  if (getter.sigil === "@@" && getter.capabilityArgs === undefined) {
    const vals = node.aspects.get(getter.path.join("."));
    if (!vals || vals.length === 0) return [];
    // Distinct, sorted — deterministic container assignment.
    return [...new Set(vals.map(valueLabel).filter((s): s is string => s !== undefined))];
  }
  const scalar = evalExpression(getter as Expr, node, ctx);
  return scalar === undefined ? [] : [String(scalar)];
}

/** A model `Value` → its group/label string. */
function valueLabel(v: Value): string | undefined {
  switch (v.kind) {
    case "string":
    case "identifier":
      return v.text;
    case "ref":
      return v.instance ? `${v.path.join(".")} as ${v.instance}` : v.path.join(".");
    case "number":
      return String(v.value);
    case "boolean":
      return String(v.value);
    case "list":
    case "expr":
      return undefined; // neither a nested list nor an expression is a group value
  }
}

// =============================================================================
// style clauses — the style engine (S14)
// =============================================================================

/** Style-engine diagnostic codes (surfaced by S15). */
export const VIEW_COLOR_NEEDS_CAST = "VIEW_COLOR_NEEDS_CAST";
export const VIEW_UNKNOWN_BUNDLE = "VIEW_UNKNOWN_BUNDLE";
export const VIEW_UNKNOWN_POLICY = "VIEW_UNKNOWN_POLICY";
export const VIEW_CSS_UNPARSEABLE = "VIEW_CSS_UNPARSEABLE";

/** The full result of evaluating every `style` clause of a view. */
interface StyleEvalResult {
  readonly styleIndex: Map<string, NodeStyle>;
  readonly edgeStyleIndex: Map<string, EdgeStyle>;
  /** `style <arrow-with-outside>` clauses — applied to boundary stubs after
   *  the marks are computed (clause order; later rules win). */
  readonly boundaryStyleRules: {
    readonly nodes: ReadonlySet<string>;
    readonly direction: "in" | "out" | "both";
    readonly style: EdgeStyle;
  }[];
  readonly colorResult: Map<string, string>;
  readonly legend: ColorLegendEntry[];
  readonly pins: Map<string, { x: number; y: number }>;
  /** `style @@<key> { render: replicated }` — aspect plane → projection mode. */
  readonly renderModeByAspect: Map<string, "web" | "replicated">;
  readonly diagnostics: Diagnostic[];
}

/**
 * Evaluate every `style` clause of a view into the style-engine outputs: node
 * overlays / renames / pins (`styleIndex`), edge color/width (`edgeStyleIndex`),
 * the color casts' per-node result + legend (`colorResult`/`legend`), pin
 * positions (`pins`), and lint diagnostics. Each clause's `use <Bundle>` members
 * are expanded first; a `style violating <P>` subject resolves through the
 * policy engine (an unknown policy is a diagnostic, not a crash).
 */
function evalStyleClauses(view: ViewDeclV2, ctx: EvalContext, shown: Set<string>): StyleEvalResult {
  const out: StyleEvalResult = {
    styleIndex: new Map(),
    edgeStyleIndex: new Map(),
    boundaryStyleRules: [],
    colorResult: new Map(),
    legend: [],
    pins: new Map(),
    renderModeByAspect: new Map(),
    diagnostics: [],
  };
  let clauseIdx = 0;
  for (const c of view.clauses) {
    if (c.kind !== "style") continue;
    applyOneStyleClause(c, `c${clauseIdx++}`, ctx, shown, out);
  }
  return out;
}

function applyOneStyleClause(
  c: StyleClause,
  leafId: string,
  ctx: EvalContext,
  shown: Set<string>,
  out: StyleEvalResult
): void {
  // `use <Bundle>` — splice the named bundle's members in place before eval.
  const members = expandBundles(c.members, ctx, out.diagnostics);

  // A `style` SUBJECT takes `in <Ref>` like any other selector, and an
  // unresolvable one is the same unexplained silence a `show` gives: the rule
  // matches nothing and paints nothing, with no way to tell that from a rule
  // whose targets simply are not on screen.
  for (const bad of unresolvedInTargets(c.subject, ctx)) {
    const key = bad.reason === "unknown" ? "view.unknownInTarget" : "view.uncontainableInTarget";
    out.diagnostics.push(
      makeDiag("warning", c.subject.source ?? c.source, VIEW_UNKNOWN_IN_TARGET, key, {
        name: bad.path.join("."),
      }),
    );
  }

  // `style violating <P>` — resolve the offending set through the policy engine.
  // An unknown/out-of-scope policy is a diagnostic, never an evaluator crash.
  if (c.subject.kind === "violating") {
    const name = c.subject.policy.join(".");
    if (!ctx.policies?.has(name)) {
      out.diagnostics.push(
        makeDiag("warning", c.subject.source ?? c.source, VIEW_UNKNOWN_POLICY, "view.unknownPolicy", { name }),
      );
      return;
    }
  }

  const parts = collectStyleMembers(members);

  // `render: web | replicated` on an aspect subject (`style @@key { … }`) selects
  // the aspect projection mode for that plane — a clause-level directive, not a
  // per-node overlay. Ignored on non-aspect subjects (no plane to key on).
  if (parts.renderMode && c.subject.kind === "aspect") {
    out.renderModeByAspect.set(c.subject.path.join("."), parts.renderMode);
  }

  // `style <arrow-with-outside>` — the boundary-STUB restyle (VIEWS §7):
  // same recognized shape as the hide rules (single `>`/`<>` hop, bare
  // `outside` on exactly one side). The stub overlay is collected here and
  // applied to the marks after computeBoundary. `>>` + outside emits a
  // diagnostic (undefined semantics); other outside-arrow shapes fall through.
  const sub = c.subject;
  if (sub.kind === "arrow" && sub.hops.length === 1) {
    const hop = sub.hops[0]!;
    const headOutside = sub.head.kind === "outside";
    const rhsOutside = hop.rhs.kind === "outside";
    if (hop.op === ">>" && headOutside !== rhsOutside) {
      out.diagnostics.push(
        makeDiag("warning", c.source, VIEW_SELECTOR_ERROR, "view.selectorError", {
          detail: "`>>` (transitive closure) combined with `outside` is not supported — use `>` or `<>` for boundary-stub rules",
        }),
      );
      return;
    }
    if (hop.op !== ">>" && headOutside !== rhsOutside) {
      const patch = edgeStylePatch(parts, out);
      if (!patch) return;
      const nodeSel = headOutside ? hop.rhs : sub.head;
      let nodes: ReadonlySet<string>;
      try {
        const r = evalSelector(nodeSel, ctx);
        if (r.sort !== "node") return;
        nodes = r.nodes;
      } catch {
        return;
      }
      out.boundaryStyleRules.push({
        nodes: new Set([...nodes].filter((k) => shown.has(k))),
        direction: hop.op === "<>" ? "both" : headOutside ? "in" : "out",
        style: patch,
      });
      return;
    }
  }

  let r: SetResult;
  try {
    r = resolveSet(c.subject, ctx, shown);
  } catch (e) {
    if (e instanceof UnsupportedSelectorError) return; // unsupported subject → skip (S13 posture)
    throw e;
  }

  if (r.sort === "node") {
    applyNodeStyle([...r.nodes].sort(), parts, leafId, ctx, out);
  } else {
    applyEdgeStyle(r.edges, parts, ctx, out);
  }
}

/** A style body split into its render-typed color leaf, overlay rewrites (dotted
 *  paths, `color` excluded), and the clause-level directives. Groups are
 *  flattened (`widget { icon: … }` → `widget.icon`). */
interface StyleMembers {
  readonly color?: { readonly expr: Expr; readonly source?: SourceSpan };
  readonly overlay: readonly { readonly path: string; readonly expr: Expr }[];
  readonly rename?: string;
  readonly pin?: { readonly x: number; readonly y: number };
  /** `render: web | replicated` — the aspect projection mode. Clause-level
   *  (keyed by the clause's `@@key` subject), not a per-node overlay. */
  readonly renderMode?: "web" | "replicated";
}

function collectStyleMembers(members: readonly StyleMember[]): StyleMembers {
  let color: StyleMembers["color"];
  let rename: string | undefined;
  let pin: { x: number; y: number } | undefined;
  let renderMode: "web" | "replicated" | undefined;
  const overlay: { path: string; expr: Expr }[] = [];
  const walk = (ms: readonly StyleMember[], prefix: readonly string[]): void => {
    for (const m of ms) {
      if (m.kind === "rename") rename = m.label;
      else if (m.kind === "pin") pin = { x: m.x, y: m.y };
      else if (m.kind === "group") walk(m.members, [...prefix, ...m.path]);
      else if (m.kind === "rewrite") {
        const path = [...prefix, ...m.path].join(".");
        if (path === "color") color = { expr: m.value, source: m.source };
        else if (path === "render") renderMode = renderModeOf(m.value) ?? renderMode;
        else overlay.push({ path, expr: m.value });
      }
      // `use` is expanded away before this pass.
    }
  };
  walk(members, []);
  return { color, overlay, rename, pin, renderMode };
}

/** `render: <ident>` — read the aspect projection mode from a bare-identifier
 *  RHS (`web` | `replicated`); anything else is ignored (unknown mode). */
function renderModeOf(expr: Expr): "web" | "replicated" | undefined {
  if (expr.kind !== "literal" || expr.value.kind !== "identifier") return undefined;
  const t = expr.value.text;
  return t === "web" || t === "replicated" ? t : undefined;
}

/** Expand `use <Bundle>` members inline; unknown bundle → a diagnostic. */
function expandBundles(
  members: readonly StyleMember[],
  ctx: EvalContext,
  diagnostics: Diagnostic[]
): StyleMember[] {
  const out: StyleMember[] = [];
  for (const m of members) {
    if (m.kind !== "use") {
      out.push(m);
      continue;
    }
    const bundle: StyleBundleDecl | undefined = ctx.styleBundles?.get(m.bundle);
    if (!bundle) {
      diagnostics.push(makeDiag("warning", m.source, VIEW_UNKNOWN_BUNDLE, "view.unknownBundle", { bundle: m.bundle }));
      continue;
    }
    // Bundles are selector-free bodies; expand transitively (a bundle may `use`
    // another). No cycle guard needed for v1 — authored bundles are shallow.
    out.push(...expandBundles(bundle.members, ctx, diagnostics));
  }
  return out;
}

// -----------------------------------------------------------------------------
// node-sort styling
// -----------------------------------------------------------------------------

function applyNodeStyle(
  keys: readonly string[],
  parts: StyleMembers,
  leafId: string,
  ctx: EvalContext,
  out: StyleEvalResult
): void {
  // --- color leaf: cast (colorize/heat/css) / literal / a value → diagnostic ---
  if (parts.color) {
    applyNodeColor(keys, parts.color.expr, parts.color.source, leafId, ctx, out);
  }

  // --- widget-prop overlays (per node) + rename + pin ---
  for (const key of keys) {
    const node = ctx.universe.byKey(key);
    const overlay =
      node && parts.overlay.length > 0
        ? buildOverlay(parts.overlay, node, ctx)
        : undefined;
    if (overlay || parts.rename !== undefined || parts.pin) {
      updateNodeStyle(out.styleIndex, key, { rename: parts.rename, pin: parts.pin, overlay });
    }
    if (parts.pin) out.pins.set(key, parts.pin);
  }
}

/** Resolve every overlay rewrite against a bound node → a dotted-path → text map. */
function buildOverlay(
  rewrites: readonly { readonly path: string; readonly expr: Expr }[],
  node: ElementNode,
  ctx: EvalContext
): ReadonlyMap<string, string> | undefined {
  const overlay = new Map<string, string>();
  for (const rw of rewrites) {
    const v = exprToText(rw.expr, node, ctx);
    if (v !== undefined) overlay.set(rw.path, v);
  }
  return overlay.size > 0 ? overlay : undefined;
}

function applyNodeColor(
  keys: readonly string[],
  expr: Expr,
  source: SourceSpan | undefined,
  leafId: string,
  ctx: EvalContext,
  out: StyleEvalResult
): void {
  if (keys.length === 0) return;

  // A cast call — colorize / heat / css.
  if (expr.kind === "call" && EXPRESSION_FUNCTIONS.has(expr.fn)) {
    applyColorCast(keys, expr, source, leafId, ctx, out);
    return;
  }

  // A literal CSS color (string / bare identifier). Also keeps the S13-era
  // `styleIndex.color` channel populated for backwards compatibility.
  if (expr.kind === "literal" && (expr.value.kind === "string" || expr.value.kind === "identifier")) {
    const text = expr.value.text;
    if (!isCssColor(text)) {
      out.diagnostics.push(makeDiag("warning", source, VIEW_CSS_UNPARSEABLE, "view.cssUnparseable", { text }));
    }
    for (const k of keys) updateNodeStyle(out.styleIndex, k, { color: text });
    const { colors, legend } = assignStyled(keys, text, leafId);
    mergeColors(out, colors, legend);
    return;
  }

  // Anything else on the render-typed `color` leaf is a value, not a color.
  out.diagnostics.push(colorNeedsCastDiagnostic(expr, source));
}

function applyColorCast(
  keys: readonly string[],
  call: Extract<Expr, { kind: "call" }>,
  source: SourceSpan | undefined,
  leafId: string,
  ctx: EvalContext,
  out: StyleEvalResult
): void {
  const nodesByKey = new Map<string, ElementNode>();
  for (const k of keys) {
    const n = ctx.universe.byKey(k);
    if (n) nodesByKey.set(k, n);
  }
  const anyNode = [...nodesByKey.values()][0];
  if (!anyNode) return;

  if (call.fn === "colorize" || call.fn === "distinct") {
    const getter = call.args[0];
    if (!getter) return;
    const perNode = new Map<string, string | undefined>();
    for (const [k, n] of nodesByKey) perNode.set(k, evalToStringValue(getter, n, ctx));
    const assign = call.fn === "colorize" ? assignColorize : assignDistinct;
    mergeColorAssignment(out, assign(perNode, leafId));
    return;
  }
  if (call.fn === "heat") {
    const [getter, minE, maxE] = call.args;
    if (!getter || !minE || !maxE) return;
    const min = numberOf(evalConst(minE, anyNode, ctx));
    const max = numberOf(evalConst(maxE, anyNode, ctx));
    const label = getterLabel(getter);
    const perNode = new Map<string, number | undefined>();
    for (const [k, n] of nodesByKey) perNode.set(k, evalToNumber(getter, n, ctx));
    mergeColorAssignment(out, assignHeat(perNode, min, max, label, leafId));
    return;
  }
  if (call.fn === "css") {
    const argE = call.args[0];
    if (!argE) return;
    const perNode = new Map<string, string | undefined>();
    for (const [k, n] of nodesByKey) perNode.set(k, evalToStringValue(argE, n, ctx));
    const asn = assignCss(perNode, leafId);
    mergeColorAssignment(out, asn);
    for (const bad of asn.invalid) {
      out.diagnostics.push(makeDiag("warning", source, VIEW_CSS_UNPARSEABLE, "view.cssUnparseable", { text: bad }));
    }
  }
}

// -----------------------------------------------------------------------------
// edge-sort styling
// -----------------------------------------------------------------------------

/** The EDGE style patch of a style body (color leaf + width overlay). */
function edgeStylePatch(parts: StyleMembers, out: StyleEvalResult): EdgeStyle | undefined {
  let color: string | undefined;
  let width: number | undefined;
  if (parts.color) {
    color = edgeColorOf(parts.color.expr, parts.color.source, out);
  }
  const widthRw = parts.overlay.find((rw) => rw.path === "width");
  if (widthRw && widthRw.expr.kind === "literal") {
    const t = valueLabel(widthRw.expr.value);
    if (t !== undefined && !Number.isNaN(Number(t))) width = Number(t);
  }
  if (color === undefined && width === undefined) return undefined;
  return { ...(color !== undefined ? { color } : {}), ...(width !== undefined ? { width } : {}) };
}

function applyEdgeStyle(
  edges: ReadonlySet<DependencyEdge>,
  parts: StyleMembers,
  ctx: EvalContext,
  out: StyleEvalResult
): void {
  const patch = edgeStylePatch(parts, out);
  if (!patch) return;
  for (const e of edges) {
    if (!isCallEdge(e)) continue;
    const id = edgeIdOf(e);
    out.edgeStyleIndex.set(id, { ...out.edgeStyleIndex.get(id), ...patch });
  }
}

/** The `color` leaf on an EDGE style: a literal CSS color, `css("…")`, else a
 *  cast-explicit diagnostic (a getter/value is not a color). */
function edgeColorOf(
  expr: Expr,
  source: SourceSpan | undefined,
  out: StyleEvalResult
): string | undefined {
  if (expr.kind === "literal" && (expr.value.kind === "string" || expr.value.kind === "identifier")) {
    const text = expr.value.text;
    if (isCssColor(text)) return text;
    out.diagnostics.push(makeDiag("warning", source, VIEW_CSS_UNPARSEABLE, "view.cssUnparseable", { text }));
    return NEUTRAL_NO_DATA;
  }
  if (expr.kind === "call" && expr.fn === "css" && expr.args[0]?.kind === "literal") {
    const v = expr.args[0].value;
    const text = v.kind === "string" || v.kind === "identifier" ? v.text : undefined;
    if (text && isCssColor(text)) return text;
    out.diagnostics.push(makeDiag("warning", source, VIEW_CSS_UNPARSEABLE, "view.cssEdgeNeedsLiteral"));
    return undefined;
  }
  out.diagnostics.push(colorNeedsCastDiagnostic(expr, source));
  return undefined;
}

// -----------------------------------------------------------------------------
// helpers
// -----------------------------------------------------------------------------

/** The color-cast-explicit diagnostic: a value on the render-typed `color` leaf
 *  needs a cast wrapper. A getter reads back its own text for the suggestion. */
function colorNeedsCastDiagnostic(expr: Expr, source: SourceSpan | undefined): Diagnostic {
  if (expr.kind === "getter") {
    const g = getterLabel(expr);
    return makeDiag("error", source, VIEW_COLOR_NEEDS_CAST, "view.colorNeedsCastGetter", { getter: g });
  }
  return makeDiag("error", source, VIEW_COLOR_NEEDS_CAST, "view.colorNeedsCast");
}

function getterLabel(g: ExprGetter | Expr): string {
  if (g.kind !== "getter") return "value";
  const base = `${g.sigil}${g.path.join(".")}`;
  return g.capabilityArgs !== undefined ? `${base}(${g.capabilityArgs})` : base;
}

/** Evaluate an overlay rewrite against a node → text; a literal reads directly,
 *  a getter/value evaluates (unresolved / unsupported → undefined, dropped). */
function exprToText(expr: Expr, node: ElementNode, ctx: EvalContext): string | undefined {
  if (expr.kind === "literal") return valueLabel(expr.value);
  try {
    const v = evalExpression(expr, node, ctx);
    return v === undefined ? undefined : String(v);
  } catch (e) {
    if (e instanceof UnsupportedExprError) return undefined;
    throw e;
  }
}

/** Evaluate a getter to a string group value for colorize/css (list-valued or
 *  unsupported → undefined, i.e. the "no data" bucket). */
function evalToStringValue(expr: Expr, node: ElementNode, ctx: EvalContext): string | undefined {
  try {
    const v = evalExpression(expr, node, ctx);
    return v === undefined ? undefined : String(v);
  } catch (e) {
    if (e instanceof UnsupportedExprError) return undefined;
    throw e;
  }
}

/** Evaluate a getter to a number for heat (undefined / non-numeric → undefined). */
function evalToNumber(expr: Expr, node: ElementNode, ctx: EvalContext): number | undefined {
  try {
    const v = evalExpression(expr, node, ctx);
    if (v === undefined) return undefined;
    const n = typeof v === "number" ? v : Number(v);
    return Number.isNaN(n) ? undefined : n;
  } catch (e) {
    if (e instanceof UnsupportedExprError) return undefined;
    throw e;
  }
}

/** Evaluate a constant (heat's min/max) — node-independent; a literal reads directly. */
function evalConst(expr: Expr, node: ElementNode, ctx: EvalContext): number | undefined {
  return evalToNumber(expr, node, ctx);
}

function numberOf(v: number | undefined): number {
  return v ?? 0;
}

function mergeColorAssignment(out: StyleEvalResult, asn: { colors: ReadonlyMap<string, string>; legend: readonly ColorLegendEntry[] }): void {
  mergeColors(out, asn.colors, asn.legend);
}

function mergeColors(out: StyleEvalResult, colors: ReadonlyMap<string, string>, legend: readonly ColorLegendEntry[]): void {
  for (const [k, v] of colors) out.colorResult.set(k, v);
  out.legend.push(...legend);
}

/** Merge a node-style patch into the accumulating index (overlay maps union). */
function updateNodeStyle(
  index: Map<string, NodeStyle>,
  key: string,
  patch: { color?: string; rename?: string; pin?: { x: number; y: number }; overlay?: ReadonlyMap<string, string> }
): void {
  const prev = index.get(key);
  const overlay =
    prev?.overlay || patch.overlay
      ? new Map<string, string>([...(prev?.overlay ?? []), ...(patch.overlay ?? [])])
      : undefined;
  index.set(key, {
    color: patch.color ?? prev?.color,
    rename: patch.rename ?? prev?.rename,
    pin: patch.pin ?? prev?.pin,
    overlay: overlay && overlay.size > 0 ? overlay : undefined,
  });
}
